import React from 'react';
import { Clock } from 'lucide-react';

interface UrgencyTagProps {
  /** ISO date string for the response deadline. */
  dueDate?: string | null;
  /** Threshold (days) below which the red urgent treatment shows. Spec: ≤ 5. */
  urgentWithin?: number;
  className?: string;
}

function daysUntil(dueDate?: string | null): number | null {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (isNaN(due.getTime())) return null;
  return Math.ceil((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function label(days: number): string {
  if (days < 0) return 'Past due';
  if (days === 0) return 'Closes today';
  if (days === 1) return 'Closes tomorrow';
  if (days <= 5) return `${days}d left`;
  return `${days} days`;
}

/**
 * Urgency tag — relative time to the deadline with a red clock when closing
 * soon (≤ 5 days). Urgency owns red; fit owns the green→gold→neutral ramp, so
 * the two dimensions are visually separable on every card.
 */
export function UrgencyTag({ dueDate, urgentWithin = 5, className = '' }: UrgencyTagProps) {
  const days = daysUntil(dueDate);
  if (days === null) {
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] text-bw-muted ${className}`}>
        <Clock className="h-3 w-3" />
        No due date
      </span>
    );
  }

  const urgent = days >= 0 && days <= urgentWithin;
  const pastDue = days < 0;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${
        urgent
          ? 'bg-urgent-bg text-urgent border border-urgent-border font-semibold'
          : `${pastDue ? 'text-bw-muted' : 'text-bw-muted'} font-normal`
      } ${className}`}
    >
      <Clock className={`h-3 w-3 ${urgent ? 'text-urgent' : 'text-bw-muted'}`} />
      {label(days)}
    </span>
  );
}

export default UrgencyTag;
